import { Injectable, effect, inject, untracked } from '@angular/core'
import { DOCUMENT } from '@angular/common'
import { NavigationEnd, Router } from '@angular/router'
import { LinguiService } from '@tocdk/lingui-angular'
import { filter } from 'rxjs'
import { AuthService } from '../auth/auth.service'
import type { UiLanguage } from '../auth/types'
import { isPublicSharePath } from '../branding/public-share-path'
import { HostContextService } from '../branding/host-context.service'
import { BRANDED_ADDRESS_PREFIX } from '../branding/branded-address'

/**
 * FR-050 AC-8.3: the UI locale for a profile language. `da` / `en` as chosen; any
 * other stored value (e.g. `de`, from legacy `customer_lng=german`) shows English
 * — the UI offers no other catalogue (OQ-9). Unset → null (keep the current one).
 */
export function uiLanguageFor(v: unknown): UiLanguage | null {
  if (v === 'da') return 'da'
  if (typeof v === 'string' && v !== '') return 'en'
  return null
}

/**
 * Owns the UI-locale ↔ account-preference sync (#274): hydrates the active
 * locale from the authenticated user's saved `language`, keeps <html lang> in
 * sync (a11y/SEO), and persists a user-chosen locale to /api/me/preferences
 * (no-op when logged out). Instantiated once at bootstrap (AppComponent injects
 * it) so its effects run.
 */
@Injectable({ providedIn: 'root' })
export class LocalePersistenceService {
  private readonly lingui = inject(LinguiService)
  private readonly auth = inject(AuthService)
  private readonly doc = inject(DOCUMENT)
  private readonly router = inject(Router)
  private readonly host = inject(HostContextService)

  constructor() {
    // FR-052 U2 fix round 1 (F-U2-4): a share page activates its brand's language
    // and the effect below stands down there; currentUser does not change on the way
    // back, so leaving /p/* in-app must re-assert the signed-in viewer's own language
    // (profile, else their stored choice) — never left in the brand's.
    // FR-052 U3 (F-U2-6): signed out, a branded address (/a/…) and an agency host have
    // their own language guards, so nothing here; on the Tivedo host the next page has
    // none, so the visitor's stored choice comes back, else English — activated WITHOUT
    // writing storage (AC-1.6: viewing a share never changes the stored choice).
    let onShare = false
    this.router.events.pipe(filter((e): e is NavigationEnd => e instanceof NavigationEnd)).subscribe((e) => {
      const wasShare = onShare
      onShare = isPublicSharePath(e.urlAfterRedirects)
      if (!wasShare || onShare) return
      const user = untracked(() => this.auth.currentUser())
      const stored = typeof localStorage !== 'undefined' ? localStorage.getItem('tivedo.locale') : null
      const storedUi = stored === 'da' || stored === 'en' ? stored : null
      if (!user) {
        const branded = new RegExp(`^/${BRANDED_ADDRESS_PREFIX}(?:[/?#]|$)`).test(e.urlAfterRedirects)
        if (untracked(() => this.host.isAgencyHost()) || branded) return
        const target = storedUi ?? 'en'
        if (target !== untracked(() => this.lingui.locale())) void this.lingui.activate(target).catch(() => undefined)
        return
      }
      const own = uiLanguageFor(user.language) ?? storedUi
      if (own && own !== untracked(() => this.lingui.locale())) {
        void this.lingui.activate(own).catch(() => undefined)
      }
    })
    effect(() => {
      this.doc.documentElement.lang = this.lingui.locale()
    })
    // Read current locale via untracked() so this depends ONLY on currentUser —
    // a user switch (which updates currentUser via persist) must not re-fire and
    // revert itself.
    // After login the profile language decides (FR-050 AC-8.3) — also over a
    // branded pre-login page's brand language (AC-8.2), which was never stored.
    // FR-052 U2 (AC-1.10, D3): on a public share page the payload brand's language
    // wins for signed-in viewers too, and viewing it never writes `tivedo.locale`.
    effect(() => {
      const lang = uiLanguageFor(this.auth.currentUser()?.language)
      if (untracked(() => isPublicSharePath(this.doc.location?.pathname ?? ''))) return
      if (lang && lang !== untracked(() => this.lingui.locale())) {
        void this.lingui.activate(lang).catch(() => undefined)
        if (typeof localStorage !== 'undefined') localStorage.setItem('tivedo.locale', lang)
      }
    })
  }

  /** Persist the chosen UI locale to the account; no-op when unauthenticated. */
  persist(locale: UiLanguage): void {
    if (!this.auth.isAuthed()) return
    void this.auth.updatePreferences({ language: locale }).catch(() => undefined)
  }
}
